import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * İZİN UÇLARI — 14 ucun 12'si bağlı.
 *
 * YETKİ ALTI SEVİYEDE DAĞILIYOR; şef ve müdür onayı AYRI KODLAR:
 *
 *   GET  /izin · /izin/:id      PERSONEL_GORUNTULE | IZIN_ONAY_ILK | IZIN_ONAY_SON
 *   GET  /onay-bekleyenler      IZIN_ONAY_ILK | IZIN_ONAY_SON
 *   PATCH /:id/sef-onayla       IZIN_ONAY_ILK                       ← TEK
 *   PATCH /:id/onayla           IZIN_ONAY_SON                       ← TEK
 *   PATCH /:id/reddet           IZIN_ONAY_ILK | IZIN_ONAY_SON
 *   PATCH /:id/iptal            IZIN_TALEP | IZIN_ONAY_SON
 *   GET  /bakiye/:personelId    PERSONEL_GORUNTULE | IZIN_ONAY_SON
 *   GET  /kullanmayanlar        IZIN_ONAY_SON | RAPOR_GORUNTULE
 *   GET  /benim · /bakiye/benim · PATCH /:id/gonder      IZIN_TALEP
 *
 * BAĞLANMAYAN İKİ UÇ: `POST /izin` ve `POST /izin/on-kontrol`. İkisi de
 * `izinTipiId` istiyor ama İZİN TİPİ KATALOĞU İÇİN UÇ YOK ve `gun_tipi`nin
 * aksine id'ler SABİT DEĞİL (seed `serial` kullanıyor) — sabit yazmak
 * güvensiz. Bakiye ucu da kurtarmıyor: `v_izin_bakiye` `izin_hakki`
 * tablosundan besleniyor, sabit haklı tipler (evlenme, ölüm, doğum) orada
 * hiç görünmüyor. İkisi de `IZIN_TALEP` istiyor ve PWA işidir.
 * (`IS_LISTESI.md` §3'e blocker olarak yazıldı.)
 *
 * GÜN SAYILARI METİNDİR. `isGunuSayisi` yarım gün izinlerde `0.50` olabilir;
 * sayıya çevrilirse yuvarlanıp kaybolur ve bakiye sessizce kayar.
 * `takvimGunuSayisi` ise TAM SAYI — ikisi ayrı şeydir ve birlikte gösterilir.
 */

export type IzinTalebiOzeti = Semalar['IzinTalebiOzetiDto'];
export type IzinSayfasi = Semalar['IzinSayfasiDto'];
export type BakiyeSatiri = Semalar['BakiyeSatiriDto'];
export type IzinKullanmayanSatiri = Semalar['IzinKullanmayanSatiriDto'];
export type IzinGondermeSonucu = Semalar['IzinGondermeSonucuDto'];

export type IzinDurumu = IzinTalebiOzeti['durum'];

/**
 * ONAY BEKLEYENLER — ŞEMA YANLIŞ, TİP ELLE YAZILDI.
 *
 * Kontrolcüde `@ApiOkResponse({ type: [IzinTalebiOzetiDto] })` var ama gövde
 * `{ sefOnayi, mudurOnayi }` nesnesi dönüyor:
 *
 *   return { sefOnayi: sef.kayitlar, mudurOnayi: mudur.kayitlar };
 *
 * `schema.d.ts` bu yüzden `IzinTalebiOzetiDto[]` diyor. Şemadan alınsaydı
 * `.map()` çağrısı DERLENİR ama çalışma anında `undefined` dönerdi.
 *
 * Sözleşme testi bunu yakalamadı çünkü `Ayni<Telde<Servis>, Dto>` bağı SERVİS
 * katmanına demirli; dönüşümü kontrolcü yapıyor. Backend düzeltildiğinde bu
 * arayüz silinip şemaya dönülür (`IS_LISTESI.md` §3).
 */
export interface OnayBekleyenler {
  sefOnayi: IzinTalebiOzeti[];
  mudurOnayi: IzinTalebiOzeti[];
}

/** İstemci tarafı sorgu şekli — hiçbir DTO'nun karşılığı değildir. */
export interface IzinSorgusu {
  personelId?: Kimlik;
  birimId?: Kimlik;
  durum?: IzinDurumu;
  izinTipiId?: number;
  /** Bu tarihten SONRA biten talepler. */
  baslangic?: string;
  /** Bu tarihten ÖNCE başlayan talepler. */
  bitis?: string;
  sayfa?: number;
  sayfaBoyutu?: number;
}

export const DURUM_ETIKETI: Record<IzinDurumu, string> = {
  taslak: 'Taslak',
  sef_onayi: 'Şef onayında',
  mudur_onayi: 'Müdür onayında',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
  iptal: 'İptal',
};

/* ------------------------------------------------------------------ */

export function izinListesi(sorgu: IzinSorgusu = {}): Promise<IzinSayfasi> {
  return istek<IzinSayfasi>('/v1/izin', { sorgu: { ...sorgu } });
}

export function izinTalebi(id: Kimlik): Promise<IzinTalebiOzeti> {
  return istek<IzinTalebiOzeti>(`/v1/izin/${id}`);
}

export function onayBekleyenler(): Promise<OnayBekleyenler> {
  return istek<OnayBekleyenler>('/v1/izin/onay-bekleyenler');
}

export function izinBakiyesi(personelId: Kimlik): Promise<BakiyeSatiri[]> {
  return istek<BakiyeSatiri[]>(`/v1/izin/bakiye/${personelId}`);
}

export function izinKullanmayanlar(): Promise<IzinKullanmayanSatiri[]> {
  return istek<IzinKullanmayanSatiri[]>('/v1/izin/kullanmayanlar');
}

/* --- Kendi izinlerim (IZIN_TALEP) ----------------------------------- */

/**
 * `benim` ve `bakiye/benim` AYRI UÇLARDIR.
 *
 * `bakiye/:personelId` `ben` değerini kabul ediyor ama `PERSONEL_GORUNTULE` /
 * `IZIN_ONAY_SON` istiyor; o kodlar sıradan personelde YOK ve kendi bakiyesini
 * göremiyordu.
 */
export function izinlerimListesi(): Promise<IzinSayfasi> {
  return istek<IzinSayfasi>('/v1/izin/benim');
}

export function bakiyemListesi(): Promise<BakiyeSatiri[]> {
  return istek<BakiyeSatiri[]>('/v1/izin/bakiye/benim');
}

/**
 * Taslağı onay zincirine gönderir.
 *
 * Dönüş YENİ DURUMU taşır: eşzamanlı izin sınırı aşılmıyorsa doğrudan
 * `mudur_onayi`, aşılıyorsa önce `sef_onayi`. Gösterilmezse kullanıcı hangi
 * basamakta olduğunu bilemez.
 */
export function izinGonder(id: Kimlik): Promise<IzinGondermeSonucu> {
  return istek<IzinGondermeSonucu>(`/v1/izin/${id}/gonder`, { yontem: 'PATCH' });
}

/* --- Onay ve red ---------------------------------------------------- */

/** `aciklama` İSTEĞE BAĞLI (`OnayDto`) — red ve iptalin aksine. */
export function izinSefOnayla(id: Kimlik, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/izin/${id}/sef-onayla`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

export function izinMudurOnayla(id: Kimlik, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/izin/${id}/onayla`, { yontem: 'PATCH', govde: { aciklama } });
}

/** Gerekçe ZORUNLU (`@IsNotEmpty`) — kayıt silinmez, gerekçe kaydedilir. */
export function izinReddet(id: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/izin/${id}/reddet`, { yontem: 'PATCH', govde: { gerekce } });
}

/**
 * Talebi iptal eder. BAŞLAMIŞ İZİN İPTAL EDİLEMEZ — sunucu 400 döndürür.
 *
 * Geriye dönük iptal kullanılmış izni bakiyeye geri ekler ve o günleri
 * devamsızlığa çevirir.
 */
export function izinIptal(id: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/izin/${id}/iptal`, { yontem: 'PATCH', govde: { gerekce } });
}
